/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file supportUnreadStore.js
 * @description
 * Shared (module-singleton) live-support unread counter for the messenger hub
 * and every chat entry point. One store for the whole app, so the many header
 * buttons mounted at once share ONE request instead of each polling:
 *  - first subscriber triggers a fetch + starts a slow poll (2 min) and an
 *    AppState "became active" refresh;
 *  - refreshes are throttled ({@link MIN_REFRESH_MS}) unless forced (the hub
 *    forces one on focus, i.e. after the user returns from the support chat);
 *  - signed-out / failing calls resolve to 0 (never throws, never blocks UI).
 *
 * Source: `GET /api/support-chat/conversations/mine?active=true` →
 * Σ `unreadForUser` of the open conversations (see chatHub.supportUnreadFrom).
 */

import {useEffect, useState} from 'react';
import {AppState} from 'react-native';
import {getMySupportConversations} from '../../services/supportChatApi';
import {supportUnreadFrom} from './chatHub';

/** Minimum gap between two non-forced refreshes. */
const MIN_REFRESH_MS = 30 * 1000;
/** Background poll cadence while at least one consumer is mounted. */
const POLL_MS = 2 * 60 * 1000;

/**
 * @typedef {{count: number, conversations: Array, loadedAt: number, error: boolean}} SupportUnreadState
 */

/** @type {SupportUnreadState} */
let state = {count: 0, conversations: [], loadedAt: 0, error: false};
const listeners = new Set();
let inflight = null;
let pollTimer = null;
let appStateSub = null;

/** Push the current state to every subscriber. */
const emit = () => {
    listeners.forEach((listener) => {
        try {
            listener(state);
        } catch (_) {
            // a broken listener must not break the others
        }
    });
};

/**
 * Current snapshot (no fetch).
 * @returns {SupportUnreadState}
 */
export const getSupportUnreadSnapshot = () => state;

/**
 * Refresh the counter from the backend.
 * @param {{force?: boolean}} [options]
 * @returns {Promise<SupportUnreadState>}
 */
export const refreshSupportUnread = async ({force = false} = {}) => {
    if (inflight) {
        return inflight;
    }
    if (!force && state.loadedAt && Date.now() - state.loadedAt < MIN_REFRESH_MS) {
        return state;
    }
    inflight = (async () => {
        try {
            const conversations = await getMySupportConversations({active: true});
            state = {count: supportUnreadFrom(conversations), conversations, loadedAt: Date.now(), error: false};
        } catch (_) {
            // Signed out (AUTH_REQUIRED) or backend failure → no badge.
            state = {count: 0, conversations: [], loadedAt: Date.now(), error: true};
        } finally {
            inflight = null;
        }
        emit();

        return state;
    })();

    return inflight;
};

/** Start the poll + foreground refresh (first subscriber). */
const start = () => {
    if (!pollTimer) {
        pollTimer = globalThis.setInterval(() => {
            if (AppState.currentState === 'active') {
                refreshSupportUnread();
            }
        }, POLL_MS);
    }
    if (!appStateSub) {
        appStateSub = AppState.addEventListener?.('change', (next) => {
            if (next === 'active') {
                refreshSupportUnread();
            }
        }) || null;
    }
};

/** Stop the poll (last subscriber left). */
const stop = () => {
    if (pollTimer) {
        globalThis.clearInterval(pollTimer);
        pollTimer = null;
    }
    appStateSub?.remove?.();
    appStateSub = null;
};

/**
 * Reset to zero (e.g. on logout). Exposed for tests / logout hooks.
 * @returns {void}
 */
export const resetSupportUnread = () => {
    state = {count: 0, conversations: [], loadedAt: 0, error: false};
    emit();
};

/**
 * Hook: live-support unread state, shared across the app.
 * @param {{enabled?: boolean}} [options] `enabled:false` skips subscribing (returns zeros).
 * @returns {SupportUnreadState}
 */
export const useSupportUnread = ({enabled = true} = {}) => {
    const [snapshot, setSnapshot] = useState(state);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        listeners.add(setSnapshot);
        start();
        setSnapshot(state);
        refreshSupportUnread();

        return () => {
            listeners.delete(setSnapshot);
            if (!listeners.size) {
                stop();
            }
        };
    }, [enabled]);

    return enabled ? snapshot : {count: 0, conversations: [], loadedAt: 0, error: false};
};

export default useSupportUnread;
